import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import api, { type ApiError } from '../../apiService';
import {
    CHANGE_PASSWORD_FALLBACK_USER_MESSAGE,
    CHANGE_PASSWORD_SERVICE_USER_MESSAGE,
    CHANGE_PASSWORD_SUCCESS_USER_MESSAGE,
    SESSION_EXPIRED_USER_MESSAGE,
    WEAK_PASSWORD_USER_MESSAGE,
} from '../../apiErrors';
import { startKeycloakPasswordChange } from '../../auth/oidc';
import DialogShell from '../ui/DialogShell';

interface ChangePasswordModalProps {
    onClose: () => void;
    onSessionExpired: (message: string) => void;
    authToken: string;
    /**
     * BAR-1564: the password lives in Keycloak. The dialog then only explains
     * and continues to Keycloak's own form (`kc_action=UPDATE_PASSWORD`); App
     * finishes the change when the browser comes back.
     */
    keycloak?: boolean;
}

function ChangePasswordModal({ onClose, onSessionExpired, authToken, keycloak = false }: ChangePasswordModalProps) {
    if (keycloak) return <KeycloakChangePassword onClose={onClose} />;
    return <FirebaseChangePassword onClose={onClose} onSessionExpired={onSessionExpired} authToken={authToken} />;
}

function KeycloakChangePassword({ onClose }: { onClose: () => void }) {
    const titleId = React.useId();
    const continueRef = useRef<HTMLButtonElement>(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [message, setMessage] = useState('');

    // Back from Keycloak through the back/forward cache: offer Continue again.
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) setIsRedirecting(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const handleContinue = async () => {
        setIsRedirecting(true);
        setMessage('');
        try {
            await startKeycloakPasswordChange(window.location.pathname + window.location.search + window.location.hash);
        } catch (error: unknown) {
            console.error('[Auth] Could not open the password change:', error);
            setMessage(CHANGE_PASSWORD_SERVICE_USER_MESSAGE);
            setIsRedirecting(false);
        }
    };

    return (
        <DialogShell
            open
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy="dismiss"
            initialFocusRef={continueRef}
            overlayClassName="flex items-center justify-center p-4"
            panelClassName="w-full max-w-md relative overflow-hidden"
        >
                <button type="button" onClick={onClose} aria-label="Close change password" className="absolute top-3 right-3 p-1 rounded-full text-fg-tertiary hover:bg-surface-sunken">
                    <X size={24} strokeWidth={1.5} aria-hidden="true" />
                </button>
                <div className="p-8">
                    <h2 id={titleId} className="text-center text-xl font-semibold tracking-tight text-fg">
                        Change your password
                    </h2>
                    {message && (
                        <p role="alert" aria-atomic="true" className="text-center text-sm mt-4 text-danger">
                            {message}
                        </p>
                    )}
                    <p className="mt-6 text-sm text-fg-secondary text-center">
                        You change your password on the BARK sign-in page. When it is done, you come back here.
                    </p>
                    <button
                        ref={continueRef}
                        type="button"
                        onClick={handleContinue}
                        disabled={isRedirecting}
                        className="mt-8 group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-on-primary hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                    >
                        {isRedirecting && (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} aria-hidden="true" />
                        )}
                        {isRedirecting ? 'Opening…' : 'Continue'}
                    </button>
                </div>
        </DialogShell>
    );
}

function FirebaseChangePassword({ onClose, onSessionExpired, authToken }: Omit<ChangePasswordModalProps, 'keycloak'>) {
    const titleId = React.useId();
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageVersion, setMessageVersion] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => () => {
        if (successTimerRef.current !== null) {
            clearTimeout(successTimerRef.current);
        }
    }, []);

    const showMessage = (nextMessage: string) => {
        setMessage(nextMessage);
        setMessageVersion(version => version + 1);
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        let sessionTransitionStarted = false;
        setIsLoading(true);
        setMessage('');

        if (!currentPassword || !newPassword || !confirmPassword) {
            showMessage('Enter all password fields.');
            setIsLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            showMessage('New passwords do not match.');
            setIsLoading(false);
            return;
        }

        if (newPassword.length < 8) {
            showMessage(WEAK_PASSWORD_USER_MESSAGE);
            setIsLoading(false);
            return;
        }

        if (currentPassword === newPassword) {
            showMessage('New password must be different from current password.');
            setIsLoading(false);
            return;
        }

        try {
            const result = await api.changePassword(authToken, currentPassword, newPassword);
            showMessage(result.message || CHANGE_PASSWORD_SUCCESS_USER_MESSAGE);
            setIsSuccess(true);
            successTimerRef.current = setTimeout(() => {
                successTimerRef.current = null;
                onClose();
            }, 2000);
        } catch (error: unknown) {
            const apiError = error as ApiError;
            if (apiError.requiresLogin === true) {
                sessionTransitionStarted = true;
                onSessionExpired(apiError.userMessage || SESSION_EXPIRED_USER_MESSAGE);
                return;
            }
            showMessage(apiError.userMessage || CHANGE_PASSWORD_FALLBACK_USER_MESSAGE);
        } finally {
            if (!sessionTransitionStarted) setIsLoading(false);
        }
    };

    return (
        <DialogShell
            open
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy={!isSuccess && (currentPassword !== '' || newPassword !== '' || confirmPassword !== '') ? 'static' : 'dismiss'}
            initialFocusRef={initialFocusRef}
            overlayClassName="flex items-center justify-center p-4"
            panelClassName="w-full max-w-md relative overflow-hidden"
        >
                <button type="button" onClick={onClose} aria-label="Close change password" className="absolute top-3 right-3 p-1 rounded-full text-fg-tertiary hover:bg-surface-sunken">
                    <X size={24} strokeWidth={1.5} aria-hidden="true" />
                </button>
                <div className="p-8">
                    <h2 id={titleId} className="text-center text-xl font-semibold tracking-tight text-fg">
                        Change your password
                    </h2>
                    {message && (
                        <p
                            key={`${isSuccess ? 'status' : 'alert'}-${messageVersion}`}
                            role={isSuccess ? 'status' : 'alert'}
                            aria-atomic="true"
                            className={`text-center text-sm mt-4 ${isSuccess ? 'text-success' : 'text-danger'}`}
                        >
                            {message}
                        </p>
                    )}

                    {!isSuccess && (
                        <form onSubmit={handleChangePassword} className="mt-8 space-y-6">
                            <div className="space-y-4">
                                <input
                                    ref={initialFocusRef}
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    type="password"
                                    name="current-password"
                                    autoComplete="current-password"
                                    aria-label="Current password"
                                    className="relative block w-full appearance-none rounded-md border border-border-default px-3 py-3 text-fg placeholder-fg-tertiary focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="Current password"
                                />
                                <input
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    type="password"
                                    name="new-password"
                                    autoComplete="new-password"
                                    aria-label="New password"
                                    className="relative block w-full appearance-none rounded-md border border-border-default px-3 py-3 text-fg placeholder-fg-tertiary focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="New password"
                                />
                                <input
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    type="password"
                                    name="confirm-password"
                                    autoComplete="new-password"
                                    aria-label="Confirm new password"
                                    className="relative block w-full appearance-none rounded-md border border-border-default px-3 py-3 text-fg placeholder-fg-tertiary focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="Confirm new password"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                aria-label={isLoading ? 'Changing password' : undefined}
                                className="group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-on-primary hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                            >
                                {isLoading && (
                                    <Loader2
                                        className="h-4 w-4 animate-spin"
                                        data-testid="change-password-spinner"
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                )}
                                {isLoading ? 'Changing password…' : 'Change password'}
                            </button>
                        </form>
                    )}
                </div>
        </DialogShell>
    );
}

export default ChangePasswordModal;
